import { QueryClient } from '@tanstack/react-query';
import type { AppRouter } from '@tennis-scanner/api-types';
import { type CreateTRPCClient, httpBatchLink, loggerLink } from '@trpc/client';
import { type CreateTRPCReact, createTRPCReact } from '@trpc/react-query';
import { supabase } from './supabase';

// tRPC + react-query glue.
//
// Usage in components:
//   const { data, isLoading } = trpc.opportunities.list.useQuery(
//     { tenantId, limit: 20 },
//     { refetchInterval: 5000 },          // polling cadence
//   );
//   const mutate = trpc.signals.createManual.useMutation();
//
// AppRouter is imported from `@tennis-scanner/api-types`, which re-exports
// the type-only shape from apps/api. CLAUDE.md / Section 0.4 forbids
// importing apps from apps; the api-types shim is the only sanctioned bridge.
//
// Auth: every request gets the current Supabase session's access_token via
// the `headers()` async hook below. When the user signs out, supabase.auth
// returns no session and we send the request anonymously — the API will
// 401 and the front's <ProtectedRoute /> guard will route to /login.

export const trpc: CreateTRPCReact<AppRouter, unknown> = createTRPCReact<AppRouter>();

// react-query default config tuned for an operator UI:
//   - retry: 1  → networking blip is retried once, hard 4xx surfaces fast.
//   - staleTime: 30s → views don't refetch on every focus; explicit polling
//                       handles freshness for live-data views.
//   - refetchOnWindowFocus: false → prevents flicker when alt-tabbing.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const API_URL = import.meta.env.VITE_API_URL;
if (!API_URL) {
  throw new Error(
    'VITE_API_URL is not set. Copy apps/scanner-web/.env.example to .env.local and fill it in.',
  );
}

// Refresh the access token if it expires within this many seconds. Default
// Supabase TTL is 3600s (60 min); supabase-js's autoRefreshToken background
// timer normally handles this. The explicit check below covers two gaps:
//   1. Laptop sleep — JS timers don't fire while suspended, so the proactive
//      refresh can be skipped. We catch up on the next call.
//   2. Long-running tabs where the timer drifts (browser throttling).
// 60s gives headroom for clock skew + the round-trip to Supabase Auth.
const REFRESH_BEFORE_EXPIRY_SEC = 60;

// One client per app boot. Recreating across renders defeats batching.
export const trpcClient: CreateTRPCClient<AppRouter> = trpc.createClient({
  links: [
    // Logs failed calls to the console in dev only — strip in prod via env.
    loggerLink({
      enabled: (opts) =>
        (import.meta.env.DEV && typeof window !== 'undefined') ||
        (opts.direction === 'down' && opts.result instanceof Error),
    }),
    httpBatchLink({
      url: `${API_URL}/trpc`,
      // Per-request token fetch with proactive refresh. supabase.auth stores
      // the session in localStorage (key: 'tennis-scanner-auth' — see
      // lib/supabase.ts) and refreshes it on a timer; we top that up here
      // for the post-sleep edge case where the timer didn't fire.
      async headers() {
        const { data } = await supabase.auth.getSession();
        let session = data.session;
        if (session?.expires_at) {
          const secsUntilExpiry = session.expires_at - Math.floor(Date.now() / 1000);
          if (secsUntilExpiry < REFRESH_BEFORE_EXPIRY_SEC) {
            const refreshed = await supabase.auth.refreshSession();
            // If refresh fails (refresh_token expired / revoked), drop to no
            // header so the API returns 401 → ProtectedRoute kicks user to
            // /login. Better than sending a stale token and looping.
            session = refreshed.data.session;
          }
        }
        const token = session?.access_token;
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    }),
  ],
});
